export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported")
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register("/service-worker", {
      scope: "/",
    })

    console.log("Service worker registration started:", registration.scope)

    if (registration.installing) {
      console.log("Service worker installing...")
      await new Promise<void>((resolve) => {
        const worker = registration.installing
        if (!worker) {
          resolve()
          return
        }

        worker.addEventListener("statechange", () => {
          if (worker.state === "installed") {
            console.log("Service worker installed")
            resolve()
          } else if (worker.state === "activated") {
            console.log("Service worker activated")
            resolve()
          } else if (worker.state === "redundant") {
            console.warn("Service worker became redundant")
            resolve()
          }
        })
      })
    } else if (registration.waiting) {
      console.log("Service worker waiting")
    } else if (registration.active) {
      console.log("Service worker already active")
    }

    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            console.log("New service worker available")
          }
        })
      }
    })

    return registration
  } catch (error) {
    console.error("Service worker registration failed:", error)
    if (error instanceof Error) {
      console.error("Registration error details:", error.message, error.stack)
    }
    return null
  }
}

export async function waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    return null
  }

  try {
    return await navigator.serviceWorker.ready
  } catch (error) {
    console.error("Failed to get service worker ready:", error)
    return null
  }
}

export async function isServiceWorkerRegistered(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    return false
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    if (registrations.length === 0) {
      return false
    }

    const registration = registrations[0]
    return registration.active !== null || registration.installing !== null || registration.waiting !== null
  } catch {
    return false
  }
}

export async function getServiceWorkerStatus(): Promise<{
  registered: boolean
  active: boolean
  error?: string
}> {
  if (!("serviceWorker" in navigator)) {
    return {
      registered: false,
      active: false,
      error: "Service workers are not supported in this browser",
    }
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    if (registrations.length === 0) {
      return {
        registered: false,
        active: false,
        error: "Service worker is not registered. Please refresh the page.",
      }
    }

    const registration = registrations[0]
    const active = registration.active !== null

    if (!active && registration.installing) {
      return {
        registered: true,
        active: false,
        error: "Service worker is installing. Please wait and try again.",
      }
    }

    if (!active && registration.waiting) {
      return {
        registered: true,
        active: false,
        error: "Service worker is waiting to activate. Please close all tabs and reopen the app.",
      }
    }

    return {
      registered: true,
      active,
    }
  } catch (error) {
    return {
      registered: false,
      active: false,
      error: error instanceof Error ? error.message : "Unknown error checking service worker status",
    }
  }
}
